import { EPermission } from 'api/core';
import { ReactNode } from 'react';
import { Link } from 'react-router';

export interface Section {
  title: string;
  description: string;
  permissions: EPermission[];
  entries: Entry[];
}

export interface Entry {
  slug: string;
  title: string;
  permissions: EPermission[];
  content: ReactNode;
}

export const sections: Section[] = [
  {
    title: 'Generelt',
    description: 'Her finder du svar på generelle spørgsmål om Smart Create.',
    permissions: [],
    entries: [
      {
        title: 'Brugerstyring',
        slug: 'brugerstyring',
        permissions: [EPermission.UserWrite],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Brugerstyring er nemt og overskueligt i Smart Create. Som Manager
              af en butik har du selv mulighed for at oprette, redigere og
              fjerne brugere. Du kan også tildele roller og rettigheder til hver
              bruger, hvilket giver dig fuld kontrol over, hvem der har adgang
              til hvilke funktioner i systemet. Du kan tilgå brugerstyringen for
              din butik ved at klikke{' '}
              <Link className="text-primary underline" to="/organization/users">
                her
              </Link>
              .
            </p>
          </div>
        ),
      },
    ],
  },
  {
    title: 'Boligvælger',
    description: 'Her finder du svar på spørgsmål om Boligvælger.',
    permissions: [EPermission.ModuleBoligvaelger],
    entries: [],
  },
  {
    title: 'Smart Video',
    description: 'Her finder du svar på spørgsmål om Smart Video.',
    permissions: [EPermission.ModuleMediaOrbit],
    entries: [
      {
        title: 'Smart Video som redigeringsværktøj',
        slug: 'smart-video-redigeringsvaerktoej',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Smart Video er ikke tænkt som et avanceret redigeringsværktøj,
              hvor mæglere skal bruge lang tid på at finjustere detaljer. Smart
              Video automatiserer redigeringsprocessen og sparer mægleren mest
              mulig tid.
            </p>
            <p>
              Det er et helt bevidst valg at undlade funktionalitet, som fx
              manuelle justeringer, som man ser i et værktøj som fx Canva, da
              mægleren jo ikke skal bruge en helt masse tid på at håndtere små
              detaljer. I stedet sørger Smart Video automatisk for de nødvendige
              justeringer og redigeringer, så videoen hurtigt er klar til brug.
            </p>
            <p>
              Formålet med Smart Video er en enkel, effektiv og tidsbesparende
              video-løsning, hvor mægleren kan fokusere på det vigtigste, såsom
              - at præsentere boligen - uden at skulle bruge en masse tid på
              redigering.
            </p>
          </div>
        ),
      },
      {
        title: 'Baggrundsmusik',
        slug: 'baggrundsmusik',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Smart Video tilbyder en række royaltyfrie musiknumre, som kan
              bruges i dine videoer. Disse numre er udvalgt for at sikre, at du
              kan tilføje en professionel lyd til dine videoer uden at bekymre
              dig om ophavsret.
            </p>
            <p>
              Du kan også uploade dine egne musiknumre, hvis du ønsker at bruge
              noget, der ikke er tilgængeligt i Smart Video. Du kan uploade
              filer ved at gå over på{' '}
              <Link
                className="text-primary underline"
                to="/organization/assets"
              >
                Alle filer
              </Link>{' '}
              og klikke upload fil. Når du har uploadet musikken, kan du vælge
              den i Smart Video og bruge den i dine videoer.
            </p>
          </div>
        ),
      },
      {
        title: 'Videokvalitet',
        slug: 'videokvalitet',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Når du uploader en video i Smart Video, bliver den automatisk
              nedskaleret og komprimeret. Dette betyder, at den færdige video
              ikke nødvendigvis vil være i samme kvalitet som originalen.
              Årsagen til dette er, at sociale medier og de fleste andre
              platforme alligevel nedskalerer videoer, hvilket vil resultere i
              samme format og kvalitet. Vi gør det egentlig bare mere Smart.
            </p>
            <p>
              Hvis du uploader en højkvalitetsvideo, fx i 4K, til Facebook, vil
              Facebook selv nedskalere videoens kvalitet. Derfor vil det
              endelige resultat være præcis det samme, uanset om du uploader en
              original video i høj opløsning eller en komprimeret version i
              Smart Video.
            </p>
            <p>
              Ved at nedskalere videoen allerede i Smart Video opnår vi
              hurtigere redigeringstider, fordi det er betydeligt mere
              ressourcekrævende at redigere videoer i fx 4K. Det færdige
              resultat afviger ikke synderligt, da fx Facebook gør det samme og
              alligevel komprimerer videoen yderligere ved upload.
            </p>
          </div>
        ),
      },
      {
        title: 'Lydkvalitet og støjreduktion',
        slug: 'lydkvalitet-og-stoejreduktion',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Smart Video har en funktion, der forsøger at fjerne baggrundsstøj
              fra videoen for at forbedre lydkvaliteten. I de fleste tilfælde
              fungerer dette rigtig godt og resulterer i en mere klar og tydelig
              lyd.
            </p>
            <p>
              Dog er der visse situationer, hvor støjreduktionen kan have sine
              begrænsninger. Hvis videoen eksempelvis er optaget tæt på en
              stærkt trafikeret vej eller i stormvejr, kan baggrundsstøjen være
              så markant, at vores støjreduktion ikke kan fjerne støjen
              fuldstændigt.
            </p>
            <p>
              Teamet arbejder løbende på at forbedre Smart Videos algoritmer for
              at kunne håndtere mere udfordrende lydforhold, men i ekstreme
              tilfælde vil der desværre stadig kunne forekomme hørbar støj.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    title: 'Smart Text',
    description: 'Her finder du svar på spørgsmål om Smart Text.',
    permissions: [EPermission.ModuleSmartText],
    entries: [
      {
        title: 'Upload billeder',
        slug: 'upload-billeder',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Det er i øjeblikket ikke muligt at uploade billeder til brug i
              tekstgenereringen i Smart Text. Modellen arbejder udelukkende ud
              fra boligens stamdata, såvel som den angivne skrivestil, mv.
            </p>
            <p>
              Vi arbejder dog på at understøtte billedbaseret input i fremtiden,
              så Smart Text automatisk kan tage højde for billedindtryk og
              visuelle detaljer i sine forslag.
            </p>
          </div>
        ),
      },
      {
        title: 'Formater',
        slug: 'formater',
        permissions: [],
        content: (
          <div className="flex flex-col gap-4">
            <p>
              Som standard tilbyder Smart Text tre formater:{' '}
              <b>Sociale medier</b>, <b>Hjemmeside</b> og <b>Butiksvinduer</b>.
              Disse dækker de mest almindelige anvendelser, og hver type er
              tilpasset til platformens krav og kommunikationsform.
            </p>
            <p>
              Har jeres butik behov for flere formater, er I meget velkomne til
              at oprette en{' '}
              <Link
                to="/organization/service-tickets"
                className="text-primary underline"
              >
                supportsag
              </Link>
              . Vi opretter gerne ekstra formater for jer, så I får præcis det
              output, I har brug for.
            </p>
          </div>
        ),
      },
    ],
  },
];
